import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TailSpin } from "react-loader-spinner";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import { Container, Divider, IconButton, MenuItem, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import { useGetActiveOrganizationsQuery, useGetOrgByIdQuery } from "state/rtkQuery/org/orgQueries";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import { useUpdateUserMembershipBulkMutation, useUpdateUserOrgStatusMutation } from "state/rtkQuery/user/userMutations";
import { useGetCostCenterUsersQuery, useGetUsersByOrgAndStatusQuery, useLazyGetUserQuery } from "state/rtkQuery/user/userQueries";

import AdminDataGrid from "./components/AdminDataGrid/AdminDataGrid";
import AdminUserActions from "./components/AdminUserActions/AdminUserActions";
import MultiSelectModal from "./components/MultiSelectModal/MultiSelectModal";
import ReasonColumn from "./components/ReasonColumn/ReasonColumn";
import UserDetailsModal from "./components/UserDetailsModal/UserDetailsModal";

import { getMSALToken } from "../../utils/getMSALToken";
import { useDebounce } from "../../utils/useDebounce";

import "./AdminDashboard.css";

const AdminDashboard = ({ tabsSlot, ...props }) => {
  const { instance, accounts } = useMsal();
  const { orgId } = useParams();

  const [authToken, setAuthToken] = useState();

  const userEmail = useSelector((state) => state.auth?.userAccount?.username);

  // Loader States
  const [isPageLoading, setIsPageLoading] = useState(false);

  // DataGrid data states
  const [emailSearch, setEmailSearch] = useState(""); // For default email search (no filter selected)
  const debouncedEmailSearch = useDebounce(emailSearch); // Debounced email search
  const [filterField, setFilterField] = useState(""); // Selected filter field (first_name, last_name, changed_date)
  const [filterValue, setFilterValue] = useState(""); // Value for selected filter (text or Date object)
  const debouncedFilterValue = useDebounce(filterValue); // Debounced for text fields
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [usersCostCenters, setUsersCostCenters] = useState({});

  // Multi-select data states
  const [showModalMulti, setShowModalMulti] = useState(false);
  const [multiSelectAction, setMultiSelectAction] = useState();
  const [rejectReason, setRejectReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [isMultiActionLoading, setIsMultiActionLoading] = useState(false);
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [isUserDetailsLoading, setIsUserDetailsLoading] = useState(false);
  const [userDetailsError, setUserDetailsError] = useState("");
  const [newUserById, setNewUserById] = useState({});
  const [isNewUserLookupLoading, setIsNewUserLookupLoading] = useState(false);

  // Get MSAL token
  useEffect(() => {
    getMSALToken(instance, accounts, setAuthToken);
  }, []);
  // RTK Query to get platform data
  const {
    data: platformData,
    isLoading: isPlatformDataLoading,
    isSuccess: isPlatformDataSuccess,
  } = useGetPlatformsQuery();

  const { data: allOrgs, isSuccess: isOrgsSuccess } =
    useGetActiveOrganizationsQuery();
  const { data: costCentersData, isSuccess: isCostCentersSuccess } =
    useGetCostCenterUsersQuery();

  const [triggerGetUser] = useLazyGetUserQuery();

  // RTK Query to get org data by orgId
  const { data: orgData, isLoading: isOrgDataLoading } =
    useGetOrgByIdQuery(orgId);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10); // Set your default here

  // Helper function to convert Date object to YYYY-MM-DD for date filtering
  const formatDateForAPI = useCallback((dateValue) => {
    if (!dateValue) return "";
    if (dateValue instanceof Date && !isNaN(dateValue)) {
      return format(dateValue, "yyyy-MM-dd");
    }
    return dateValue;
  }, []);

  // Convert filter value - for dates use immediate value, for text use debounced
  const formattedFilterValue = useMemo(() => {
    if (filterField === "changed_date") {
      // For date fields, use filterValue directly (Date object from picker)
      return formatDateForAPI(filterValue);
    }
    // For text fields, use debounced value to prevent API calls on every keystroke
    return debouncedFilterValue;
  }, [filterField, debouncedFilterValue, filterValue, formatDateForAPI]);

  // RTK Query to get users by org and status
  const {
    data: orgRequestsData,
    isLoading: isOrgRequestsLoading,
    isFetching: isFetchingOrgRequestsLoading,
    isSuccess: isOrgRequestsSuccess,
    isError: isOrgRequestError,
  } = useGetUsersByOrgAndStatusQuery(
    {
      orgId: orgId,
      status: "Pending",
      platformId: platformData?._id,
      page: currentPage,
      perPage: recordsPerPage,
      email: debouncedEmailSearch === "" ? undefined : debouncedEmailSearch,
      filterField: filterField || undefined,
      filterValue: formattedFilterValue === "" ? undefined : formattedFilterValue,
    },
    { skip: !isPlatformDataSuccess, pollingInterval: 300000, refetchOnMountOrArgChange: true, }
  );

  // Set rows and usersCostCenters state based on orgRequestsData
  useEffect(() => {
    if (isOrgRequestsSuccess) {
      setRows(orgRequestsData?.users || []);
    } else if (isOrgRequestError) {
      setRows([]);
    } else if (isFetchingOrgRequestsLoading) {
      setRows([]); // Clear rows while loading new page
    }
  }, [isOrgRequestsSuccess, isOrgRequestError, isFetchingOrgRequestsLoading, orgRequestsData]);

  const orgIdToName = useMemo(() => {
    if (!isOrgsSuccess || !Array.isArray(allOrgs)) {
      return {};
    }

    return allOrgs.reduce((acc, org) => {
      acc[org._id] = org.name;
      return acc;
    }, {});
  }, [allOrgs, isOrgsSuccess]);

  const costCenterById = useMemo(() => {
    const costCenters = costCentersData?.cost_centers;
    if (!isCostCentersSuccess || !Array.isArray(costCenters)) {
      return {};
    }

    return costCenters.reduce((acc, cc) => {
      acc[cc._id] = cc.name;
      return acc;
    }, {});
  }, [costCentersData, isCostCentersSuccess]);

  const handleOpenUserDetails = async (row) => {
    setShowUserDetailsModal(true);
    setIsUserDetailsLoading(true);
    setUserDetailsError("");
    try {
      const result = await triggerGetUser({
        email: row.email,
        platformId: platformData?._id,
      });
      const users = Array.isArray(result?.data) ? result.data : [];
      const exactMatch = users.find((user) => user._id === row.id);
      setSelectedUserDetails(exactMatch || users[0] || null);

      if (!users.length) {
        setUserDetailsError("Could not find user details for this request.");
      }
    } catch (error) {
      setSelectedUserDetails(null);
      setUserDetailsError("Failed to load user details. Please try again.");
    } finally {
      setIsUserDetailsLoading(false);
    }
  };

  useEffect(() => {
    let ignore = false;

    const loadNewUserIndicators = async () => {
      if (!platformData?._id || !rows.length) {
        setNewUserById({});
        return;
      }

      setIsNewUserLookupLoading(true);

      try {
        const results = await Promise.all(
          rows.map(async (row) => {
            try {
              const response = await triggerGetUser({
                email: row.email,
                platformId: platformData._id,
              });

              const users = Array.isArray(response?.data) ? response.data : [];
              const selected = users.find((item) => item._id === row.id) || users[0];
              const organizations =
                selected?.platforms?.[platformData._id]?.organizations || {};
              const hasJoinedOrg = Object.values(organizations).some(
                (org) => org?.membership_status === "Joined"
              );

              return [row.id, !hasJoinedOrg];
            } catch (error) {
              return [row.id, !!row.isNewUser];
            }
          })
        );

        if (!ignore) {
          setNewUserById(Object.fromEntries(results));
        }
      } finally {
        if (!ignore) {
          setIsNewUserLookupLoading(false);
        }
      }
    };

    loadNewUserIndicators();

    return () => {
      ignore = true;
    };
  }, [rows, platformData?._id, triggerGetUser]);



  // Set columns for MUI datagrid
  useEffect(() => {
    // if (rows && Object.keys(usersCostCenters).length > 0) {
    if (rows) {
      setColumns([
        // Requester's Name
        {
          field: "name",
          headerName: "Requester Name",
          headerClassName: "orgName-column-header",
          headerAlign: "left",
          align: "left",
          width: 230,
          renderCell: (params) => (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
              <Box sx={{ width: 16, display: "flex", justifyContent: "center", flexShrink: 0 }}>
                {(newUserById[params.row.id] ?? params.row.isNewUser) && (
                  <Tooltip sx={{fontSize:"14px"}} title="New User: Approving this request will consume 1 paid license.">
                    <PaidOutlinedIcon sx={{ color: "#00ab06", fontSize: 16 }} />
                  </Tooltip>
                )}
              </Box>
              <Tooltip title={params.row.name}>
                <span className="table-cell-trucate">{params.row.name}</span>
              </Tooltip>
            </Box>
          ),
        },
        // Requester's Email
        {
          field: "email",
          headerName: "Requester Email",
          description: "Requester Email",
          width: 200,
          headerAlign: "left",
          align: "left",
          filterable: false,
          renderCell: (params) => (
            <Tooltip title={params.row.email}>
              <span
                className="table-cell-trucate-email"
                style={{ paddingLeft: "None" }}
              >
                {params.row.email}
              </span>
            </Tooltip>
          ),
        },
        // Reason for Request
        {
          field: "reason",
          headerName: "Reason for Request",
          headerClassName: "",
          headerAlign: "center",
          align: "center",
          width: 420,
          renderCell: (params) => <ReasonColumn params={params} />,
        },
        // Cost Center field (commented out for now)
        // {
        //   field: "costCenter",
        //   headerName: "Cost Center",
        //   headerAlign: "center",
        //   align: "center",
        //   width: 190,
        //   renderCell: (params) => (
        //     <TextField
        //       sx={{
        //         "& .MuiInputBase-input": { padding: "7.5px 14px" },
        //       }}
        //       id="costCenter"
        //       select
        //       value={usersCostCenters[params.row.id]}
        //       onChange={(e) =>
        //         setUsersCostCenters((prev) => ({
        //           ...prev,
        //           [params.row.id]: e.target.value,
        //         }))
        //       }
        //       variant="outlined"
        //       style={{ width: "100%" }}
        //     >
        //       {costCenters.map((option) => (
        //         <MenuItem key={option.id} value={option.name}>
        //           {option.name}
        //         </MenuItem>
        //       ))}
        //     </TextField>
        //   ),
        // },
        // Request Date
        {
          field: "date",
          headerName: "Request Date",
          width: 140,
          align: "center",
          headerAlign: "center",
          filterable: true,
          valueGetter: (params) => {
            // Return the date as a Date object for proper sorting
            return new Date(params.row.date);
          },
          renderCell: (params) => (
            <span style={{ fontSize: "12px" }}>{params.row.date}</span>
          ),
        },
        // Actions Buttons
        {
          field: "profile",
          headerName: "Profile",
          width: 70,
          align: "center",
          headerAlign: "center",
          sortable: false,
          filterable: false,
          renderCell: (params) => (
            <Tooltip title="View user details">
              <IconButton
                size="small"
                aria-label="View user details"
                onClick={() => handleOpenUserDetails(params.row)}
                sx={{ p: 0.25, color: "#0b756c" }}
              >
                <InfoOutlinedIcon sx={{ color: "#0084ff", fontSize: 22 }} />
              </IconButton>
            </Tooltip>
          ),
        },
        // Actions Buttons
        {
          field: "action",
          headerAlign: "center",
          headerName: "Actions",
          align: "center",
          width: 110,
          filterable: false,
          renderCell: (params) => (
            <AdminUserActions
              {...{ params }}
              platformId={platformData?._id}
              orgData={orgData}
              userEmail={userEmail}
              onRequestActionHandler={handleMultiSelectAction}
              isLoading={isMultiActionLoading}
            />
          ),
        },
      ].filter(Boolean));
    }
  }, [rows, platformData?._id, orgData, userEmail, isMultiActionLoading, newUserById]);

  // Add a separate effect to reset page when email or filter changes
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [debouncedEmailSearch, formattedFilterValue]);


  useEffect(() => {
    // When a multi-action is in progress and the table refetch is done, stop loading
    if (isMultiActionLoading && !isFetchingOrgRequestsLoading) {
      setIsMultiActionLoading(false);
      setSelectedRows([]);
    }
  }, [isMultiActionLoading, isFetchingOrgRequestsLoading]);

  // RTK Query for bulk user org status (accept/reject requests) membership updates
  const [updateUserMembershipBulk, { isLoading: isUpdateUserMembershipBulkLoading }] =
    useUpdateUserMembershipBulkMutation();

  // RTK Query to update user org status (accept/reject requests) for single user
  const [updateUserOrgStatus, { isLoading: isUpdateUserOrgStatusLoading }] =
    useUpdateUserOrgStatusMutation();

  const handleMultiSelectAction = async (
    action,
    userIds = selectedRows,
    rejectReasonArg = rejectReason,
    otherReasonArg = otherReason
  ) => {
    setIsMultiActionLoading(true);
    handleClose();
    try {
      // Ensure we have an array of user IDs
      const ids = Array.isArray(userIds) ? userIds : [userIds];

      // If only one user is selected, use the single-user mutation so failures are reported correctly
      if (ids.length === 1) {
        const singlePayload = {
          platforms: {
            [platformData._id]: {
              organizations: {
                [orgData._id]: {
                  membership_status: action === "rejected" ? "Rejected" : "Requested",
                  reason:
                    action === "rejected"
                      ? rejectReasonArg === "Other"
                        ? otherReasonArg
                        : rejectReasonArg
                      : `Approved by admin ${userEmail} from ECR Portal`,
                  changed_by: userEmail.toLowerCase(),
                },
              },
            },
          },
        };

        await updateUserOrgStatus({
          userId: ids[0],
          body: singlePayload,
          attribute: "membership_status",
        }).unwrap();

        setIsMultiActionLoading(false);
        setSelectedRows([]);
        return;
      }

      // Build bulk payload for multiple selected users
      const bulkPayload = {
        platforms: {
          [platformData._id]: {
            organizations: {
              [orgData._id]: {
                membership_status: action === "rejected" ? "Rejected" : "Requested",
                reason:
                  action === "rejected"
                    ? rejectReasonArg === "Other"
                      ? otherReasonArg
                      : rejectReasonArg
                    : `Approved by admin ${userEmail} from ECR Portal`,
                changed_by: userEmail.toLowerCase(),
              },
            },
          },
        },
        user_ids: ids, // Array of selected user IDs
      };

      // Single API call for all selected users
      await updateUserMembershipBulk({
        body: bulkPayload,
        invalidateStatuses: ["Pending"],
      }).unwrap();

      // Clear selections and loading with minimum 1 second wait time
      setTimeout(() => {
        setIsMultiActionLoading(false);
        setSelectedRows([]);
      }, 1000);
    } catch (error) {
      setIsMultiActionLoading(false);
      console.error("Error updating users:", error);
    }
  };

  // Close MultiSelectModal
  const handleClose = (event, reason) => {
    if (reason === "backdropClick") {
    } else {
      setShowModalMulti(false);
      setRejectReason("");
      setOtherReason("");
    }
  };

  // Search TextField styles
  const textStyles = styled({
    root: {
      "& .Mui-focused": {
        color: "#008080",
        outline: "1px solid  #008080",
      },
    },
  });

  return (
    <Container
      sx={{ maxWidth: "1300px !important", flex: "1", marginTop: "3rem" }}
    >
      {/* If page is loading or platform/org data is loading, show loader */}
      {isPageLoading || isPlatformDataLoading || isOrgDataLoading ? (
        <Box
          sx={{
            height: 420,
            marginTop: "4rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "auto",
          }}
        >
          <TailSpin
            color="hsl(81.43deg 69.37% 43.53%)"
            height={50}
            width={50}
          />
        </Box>
      ) : (
        <div>
          <UserDetailsModal
            open={showUserDetailsModal}
            onClose={() => {
              setShowUserDetailsModal(false);
              setSelectedUserDetails(null);
              setUserDetailsError("");
            }}
            user={selectedUserDetails}
            platformId={platformData?._id}
            orgIdToName={orgIdToName}
            costCenterById={costCenterById}
            isLoading={isUserDetailsLoading}
            error={userDetailsError}
          />
          {/* Show Multi-Select confirmation modal */}
          {showModalMulti && (
            <MultiSelectModal
              show={showModalMulti}
              close={handleClose}
              selectedRows={selectedRows}
              allRows={rows}
              onRequestActionHandler={handleMultiSelectAction}
              approvalAction={multiSelectAction}
              rejectReason={rejectReason}
              setRejectReason={setRejectReason}
              otherReason={otherReason}
              setOtherReason={setOtherReason}
            />
          )}
          {/* Top */}
          <Box sx={{ height: 600 }}>
            {/* Header */}
            <div className="admin-headerSearchFlex">
              <div className="admin-dashboard-header">
                <span className="admin-dashboard-header-content">
                  <strong>{orgData?.name}</strong>
                </span>
              </div>
              {/* Search TextField */}
              <div className="admin-dashboard-search" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                {/* Filter By Label and Selector */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" sx={{ color: "#666", whiteSpace: "nowrap" }}>
                    Filter By
                  </Typography>
                  <TextField
                    select
                    size="small"
                    value={filterField}
                    onChange={(event) => {
                      setFilterField(event.target.value);
                      setEmailSearch("");
                      setFilterValue("");
                      if (currentPage !== 1) {
                        setCurrentPage(1);
                      }
                    }}
                    sx={{
                      minWidth: 120,
                      "& .MuiInputBase-root": { height: "31px" },
                    }}
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value="">Email</MenuItem>
                    <MenuItem value="first_name">First Name</MenuItem>
                    <MenuItem value="last_name">Last Name</MenuItem>
                    <MenuItem value="changed_date">Request Date</MenuItem>
                  </TextField>
                </Box>
                
                {/* Search Input - Conditional: DatePicker for dates, TextField for text */}
                {!filterField ? (
                  // No filter selected → show email search
                  <TextField
                    id="outlined-search"
                    placeholder="Search By Email"
                    type="search"
                    value={emailSearch}
                    onChange={(event) => {
                      setEmailSearch(event.target.value);
                      if (currentPage !== 1) {
                        setCurrentPage(1);
                      }
                    }}
                    className={textStyles.root}
                    variant="outlined"
                    InputProps={{
                      style: {
                        height: "31px",
                        width: "250px",
                      },
                    }}
                  />
                ) : filterField === "changed_date" ? (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label=""
                      value={filterValue || null}
                      onChange={(newValue) => {
                        setFilterValue(newValue);
                        if (currentPage !== 1) {
                          setCurrentPage(1);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Date"
                          variant="outlined"
                          sx={{ minWidth: '250px' }}
                          InputProps={{
                            ...params.InputProps,
                            style: {
                              height: "31px",
                              fontSize: "inherit",
                            }
                          }}
                          inputProps={{
                            ...params.inputProps,
                            style: {
                              ...params.inputProps?.style,
                              padding: "5px 4px",
                              fontSize: "inherit",
                            }
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                ) : (
                  <TextField
                    id="outlined-search"
                    placeholder={
                      filterField === "first_name" ? "Search by First Name" :
                      filterField === "last_name" ? "Search by Last Name" :
                      "Search by Value"
                    }
                    type="search"
                    value={filterValue}
                    className={textStyles.root}
                    variant="outlined"
                    InputProps={{
                      style: {
                        height: "31px",
                        width: "250px",
                      },
                    }}
                    onChange={(event) => {
                      setFilterValue(event.target.value);
                      if (currentPage !== 1) {
                        setCurrentPage(1);
                      }
                    }}
                  />
                )}
              </div>
            </div>
            {tabsSlot ?? (
              <Typography variant="subtitle1" style={{ marginBottom: "1rem" }}>
                Pending Approvals
              </Typography>
            )}
            <Divider />
            {/* DataGrid */}
            <AdminDataGrid
              rows={rows}
              columns={columns}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              queryData={rows}
              setShowModalMulti={setShowModalMulti}
              setMultiSelectAction={setMultiSelectAction}
              isMultiActionLoading={isUpdateUserMembershipBulkLoading || isUpdateUserOrgStatusLoading || isMultiActionLoading}
              isTableLoading={(isUpdateUserMembershipBulkLoading || isUpdateUserOrgStatusLoading || isMultiActionLoading) || isFetchingOrgRequestsLoading}
              isInitialLoading={isOrgRequestsLoading && !rows.length}
              setCurrentPage={setCurrentPage}
              currentPage={currentPage}
              totalCount={orgRequestsData?.total || 0}
              recordsPerPage={recordsPerPage}
              isNewUserLookupLoading={isNewUserLookupLoading}
            />
            {/* Footer/Support Request */}
            <div
              className="admin-dashboard-support-container"
              style={{
                marginTop: "30px",
                padding: "10px 0px",
                paddingBottom: "2rem",
              }}
            >
              <p>
                Please fill our{" "}
                <a
                  href="https://deloitteglobal.service-now.com/sp?id=sc_cat_item&sys_id=4a417b4f1b17855cbcae55b0604bcb1c"
                  target="_blank"
                  className="request-support-link"
                  rel="noreferrer"
                >
                  Support Request
                </a>
                &nbsp; Form if you face any issues.
              </p>
            </div>
          </Box>
        </div>
      )}
    </Container>
  );
};

export default AdminDashboard;
